import { createContext, PropsWithChildren, useContext, useMemo } from 'react';

import { gql, QueryResult } from '@apollo/client';

import { useAuthenticatedQuery } from 'platform/apollo';
import { LoadingPage } from 'platform/components/LoadingPage';
import { UnauthorizedError } from 'platform/Error';
import {
  CustomerAddressUUIDResponse,
  CustomerLeadWithGuaranteedAddress,
  useCustomerAddressUUID,
} from 'platform/graphql/useCustomerAddressUUID';
import { useObservability } from 'platform/observability';
import { useRedirectToLatestLead } from 'platform/useRedirectToLatestLead';

import { hasPermissionDeniedError } from 'shared/helpers/graphqlUtils';
import { useTrackGenericErrorPageViewEvent } from 'shared/routes/errors/GenericErrorPage';

import {
  GetLatestSellerLead as GetLatestSellerLeadData,
  GetLatestSellerLeadVariables,
} from 'sellDirect/graphql/__generated__/GetLatestSellerLead';
import {
  SellerLeadFragment as GeneratedSellerLeadFragment,
  SellerLeadFragment_address,
} from 'sellDirect/graphql/fragments/__generated__/SellerLeadFragment';
import { SellerLeadFragment } from 'sellDirect/graphql/fragments/SellerLeadFragment';

export const GetLatestSellerLeadQuery = gql`
  query GetLatestSellerLead($input: OdProtosSellSellerServices_GetLatestLeadRequestInput!) {
    seller {
      getLatestLead(input: $input) {
        lead {
          ...SellerLeadFragment
        }
      }
    }
  }
  ${SellerLeadFragment}
`;

/**
 * Addresses can be null on the seller lead, this guarantees it's existence
 */
export type SellerLeadWithGuaranteedAddress = GeneratedSellerLeadFragment & {
  address: SellerLeadFragment_address;
};

export type SellerLeadResponse = {
  lead?: SellerLeadWithGuaranteedAddress | null;
  sellerInputUUID: string;
  experimentEntityId: string;
  isBuyOnlyCustomer?: boolean;
  refetch?: QueryResult<GetLatestSellerLeadData>['refetch'];
} & Pick<QueryResult, 'loading' | 'error'> &
  Pick<CustomerAddressUUIDResponse, 'customerLeads'>;

export const SellerLeadContext = createContext<SellerLeadResponse>(
  {} as unknown as SellerLeadResponse,
);

/**
 * Returns the latest seller lead for a given customer and address UUID, or the seller lead for a given seller input UUID.
 * Also returns the calculated seller input UUID, customer UUID, address UUID, and experiment entity ID.
 *
 * @throws Error if no leads are found for the customer and feature flag is enabled for buy-only hub routing
 */
export function useSellerLead() {
  const value = useContext(SellerLeadContext);
  if (!value) {
    throw new Error('QualificationContext missing');
  }
  return value;
}

/**
 * SellerLeadProvider is a wrapper component that provides the latest seller lead for a given customer and address UUID.
 *
 * Renders an error page if the customer doesn't have adequate permissions to view the lead.
 * @throws UnauthorizedError if the customer doesn't have adequate permissions to view the lead
 */
export const SellerLeadProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const value = useSellerLeadQuery();

  if (value.loading) {
    return <LoadingPage from="SellerLeadProvider" reason="loading" />;
  }

  if (value.error) {
    if (hasPermissionDeniedError(value.error)) {
      throw new UnauthorizedError('seller-lead', true);
    }

    throw value.error;
  }

  return <SellerLeadContext.Provider value={value}>{children}</SellerLeadContext.Provider>;
};

/**
 * Returns the latest seller lead for a given customer and address UUID, or the seller lead for a given seller input UUID.
 * Also returns the calculated seller input UUID, customer UUID, address UUID, and experiment entity ID.
 *
 * @throws Error if no leads are found for the customer and feature flag is enabled for buy-only hub routing
 */
export const useSellerLeadQuery = (): SellerLeadResponse => {
  const { trackError } = useObservability();
  const { customerUUID, addressUUID, customerLeads, ...listLeadsByCustomerQuery } =
    useCustomerAddressUUID();
  const { trackGenericErrorPageView } = useTrackGenericErrorPageViewEvent();

  /**
   * Fetches seller lead when :customerUUID and :addressUUID are present in the URL.
   */
  const sellerLeadFromCustomerAddress = useAuthenticatedQuery<
    GetLatestSellerLeadData,
    GetLatestSellerLeadVariables
  >(GetLatestSellerLeadQuery, {
    variables: {
      input: {
        customerUuid: customerUUID as string,
        addressUuid: addressUUID as string,
      },
    },
    skip: !customerUUID || !addressUUID,
    context: { linkContext: { team: 'seller-engineering' } },
  });

  const lead = sellerLeadFromCustomerAddress.data?.seller?.getLatestLead
    ?.lead as SellerLeadWithGuaranteedAddress;

  // It's possible for Reception and Web to be out of sync and the LATEST lead not to be
  // included in the customerLeads array. If the current lead is associated with the
  // authenticated customer, we want to include it in the customerLeads array.
  const isCurrentLeadIncludedInCustomerLeads = customerLeads.some(
    (currentLead) => currentLead.address.uuid === lead?.address.uuid,
  );
  const customerLeadsWithCurrentLead = useMemo(
    () =>
      lead && !isCurrentLeadIncludedInCustomerLeads
        ? concatenateCurrentLeadToCustomer(customerLeads, lead)
        : customerLeads,
    [customerLeads, lead, isCurrentLeadIncludedInCustomerLeads],
  );

  const isBuyOnlyCustomer = customerLeadsWithCurrentLead.length === 0;

  if (lead && !lead?.address.uuid) {
    trackGenericErrorPageView('lead-missing-address-uuid', 'system-error');
    throw new Error('Lead is missing address UUID');
  }

  const loading = sellerLeadFromCustomerAddress.loading || listLeadsByCustomerQuery.loading;
  const error = sellerLeadFromCustomerAddress.error || listLeadsByCustomerQuery.error;

  const experimentEntityId = useMemo(
    () =>
      // Fetched from lead
      lead?.experimentEntityId ||
      // falls back to empty string
      '',
    [lead?.experimentEntityId],
  );

  const sellerInputUUID = lead?.source?.id || '';
  if (lead && !lead?.source?.id) {
    trackError('missing-seller-input', new Error('Missing seller input UUID on lead'));
  }

  useRedirectToLatestLead({
    loading,
    error,
    addressUUID,
    isBuyOnlyCustomer,
  });

  return {
    lead,
    sellerInputUUID,
    experimentEntityId,
    customerLeads: customerLeadsWithCurrentLead,
    isBuyOnlyCustomer,
    loading,
    error,
    refetch: !customerUUID || !addressUUID ? undefined : sellerLeadFromCustomerAddress.refetch,
  };
};

/**
 * It's possible for Reception and Web to be out of sync and the LATEST lead not to be
 * included in the customerLeads array. If the current lead is associated with the
 * authenticated customer, we want to include it in the customerLeads array.
 */
const concatenateCurrentLeadToCustomer = (
  customerLeads: CustomerLeadWithGuaranteedAddress[],
  lead: SellerLeadWithGuaranteedAddress,
): CustomerLeadWithGuaranteedAddress[] => {
  const leads = customerLeads;
  const newLead: CustomerLeadWithGuaranteedAddress = {
    __typename: 'OdProtosSellSellerData_Lead',
    id: lead.id,
    address: {
      __typename: 'OdProtosCasaData_Address',
      uuid: lead.address.uuid,
      street: lead.address.street,
      unitNumber: lead.address.unitNumber,
    },
    productOfferings: lead.productOfferings,
  };
  leads.push(newLead);

  return leads;
};
